import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Plus, Pin, Lock, MessageCircle, Eye, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

export default function ForumCategory() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [posting, setPosting] = useState(false);

  const cats = JSON.parse(typeof window !== 'undefined' && window.localStorage.getItem('forum_cats') || '[]');
  const cat = cats.find(c => c.slug === slug) || { name: slug, color: '#1d4ed8' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getForumThreads({ category_slug: slug, limit: 50 });
      setThreads(res.threads || []);
    } catch (e) {
      toast.error(e.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => {
    api.getForumCategories().then(c => {
      window.localStorage.setItem('forum_cats', JSON.stringify(c));
    }).catch(() => null);
    load();
  }, [load]);

  const submit = async () => {
    if (title.trim().length < 5) return toast.error('Titre trop court');
    if (body.trim().length < 10) return toast.error('Message trop court');
    setPosting(true);
    try {
      const res = await api.createForumThread({ category_slug: slug, title, body });
      toast.success('Discussion créée');
      setShowForm(false); setTitle(''); setBody('');
      navigate(`/reseau/forum/thread/${res.id}`);
    } catch (e) {
      toast.error(e.detail || 'Erreur');
    } finally { setPosting(false); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="space-y-4" data-testid="forum-category">
      <Button onClick={() => navigate('/reseau/forum')} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour aux catégories
      </Button>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: cat.color }}>Forum / {slug}</div>
          <h1 className="text-2xl sm:text-3xl font-black">{cat.name}</h1>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="mc-btn-primary" data-testid="new-thread-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle discussion
        </Button>
      </div>

      {showForm && (
        <div className="mc-card p-5 space-y-3" data-testid="new-thread-form">
          <input
            type="text"
            placeholder="Titre de la discussion (min 5 caractères)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-semibold"
            data-testid="thread-title-input"
          />
          <textarea
            placeholder="Votre message (min 10 caractères)…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            data-testid="thread-body-input"
          />
          <div className="flex gap-2 justify-end">
            <Button onClick={() => setShowForm(false)} variant="outline" size="sm">Annuler</Button>
            <Button onClick={submit} disabled={posting} className="mc-btn-primary" size="sm" data-testid="thread-submit">
              {posting ? 'Publication…' : 'Publier'}
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mc-card p-10 text-center text-muted-foreground">Chargement…</div>
      ) : threads.length === 0 ? (
        <div className="mc-card p-10 text-center text-sm text-muted-foreground" data-testid="forum-empty">
          Aucune discussion dans cette catégorie. Soyez le premier à en lancer une !
        </div>
      ) : (
        <div className="space-y-2">
          {threads.map(t => (
            <button
              key={t.id}
              onClick={() => navigate(`/reseau/forum/thread/${t.id}`)}
              data-testid={`forum-thread-${t.id}`}
              className={cn('mc-card p-4 text-left w-full hover:-translate-y-0.5 hover:shadow-md transition-all', t.pinned && 'border-l-4 border-l-[hsl(var(--primary))]')}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0 font-black text-sm">
                  {(t.author_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.pinned && <Pin className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}
                    {t.locked && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                    <h3 className="font-black text-sm sm:text-base">{t.title}</h3>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    par <strong>{t.author_name}</strong> · {fmtDate(t.created_at)}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-bold text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {t.post_count} réponse(s)</span>
                    <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {t.views || 0} vue(s)</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(t.last_activity_at)}</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
